import { useCallback, useEffect, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { getTags } from '../api/gg'
import { GalleryPreview } from '../components/GalleryPreview'
import { TagPicker } from '../components/TagPicker'
import { useMediaFeed } from '../hooks/useMediaFeed'
import { buildFeedTags, formatSexualitySummary } from '../lib/feedPrefs'
import { useGame } from '../store/game'
import type { ContentKind, MediaItem, Tag } from '../types'

type Phase = 'pick' | 'loading' | 'play' | 'won'

interface Card {
  id: string
  pairId: number
  item: MediaItem
}

function buildDeck(items: MediaItem[]): Card[] {
  const pool = items.filter((i) => i.src).slice(0, 8)
  if (pool.length < 4) return []
  const pairs = pool.flatMap((item, idx) => [
    { id: `${item.id}-a`, pairId: idx, item },
    { id: `${item.id}-b`, pairId: idx, item },
  ])
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[pairs[i], pairs[j]] = [pairs[j], pairs[i]]
  }
  return pairs
}

/** Memory Mode — flip tiles to find matching pairs from the NSFW tag feed. */
export function MemoryMode() {
  const setSelection = useGame((s) => s.setSelection)
  const feedPrefs = useGame((s) => s.feedPrefs)

  const [phase, setPhase] = useState<Phase>('pick')
  const [tags, setTags] = useState<Tag[]>([])
  const [tagLabels, setTagLabels] = useState<string[]>([])
  const [deck, setDeck] = useState<Card[]>([])
  const [flipped, setFlipped] = useState<number[]>([])
  const [matched, setMatched] = useState<Set<number>>(new Set())
  const [moves, setMoves] = useState(0)

  const lockRef = useRef(false)

  const { progress, loading, error, startBrowse, cancel, setError } = useMediaFeed()

  useEffect(() => {
    void getTags().then((t) => setTags(t.map((x) => ({ ...x, enabled: true, sort: 0 }))))
    return () => cancel()
  }, [cancel])

  const start = useCallback(
    async (tagIds: string[], _kind: ContentKind) => {
      const chosen = tags.filter((t) => tagIds.includes(t.id))
      if (!chosen.length) return
      setSelection(tagIds, 'image')
      setTagLabels(chosen.map((t) => t.label))
      setError(null)
      setPhase('loading')

      const feed = await startBrowse(buildFeedTags(chosen, feedPrefs), 'image', 8)
      if (!feed) {
        setPhase('pick')
        return
      }
      const cards = buildDeck(feed.queue)
      if (!cards.length) {
        setError('Need at least 4 images for Memory. Try more tags!')
        setPhase('pick')
        return
      }
      setDeck(cards)
      setFlipped([])
      setMatched(new Set())
      setMoves(0)
      setPhase('play')
    },
    [tags, setSelection, feedPrefs, startBrowse, setError],
  )

  const flip = (idx: number) => {
    const card = deck[idx]
    if (!card || lockRef.current || matched.has(card.pairId) || flipped.includes(idx)) return
    if (flipped.length >= 2) return

    const next = [...flipped, idx]
    setFlipped(next)

    if (next.length < 2) return

    setMoves((m) => m + 1)
    lockRef.current = true
    const [a, b] = next
    if (deck[a].pairId === deck[b].pairId) {
      const nm = new Set(matched)
      nm.add(deck[a].pairId)
      setMatched(nm)
      setFlipped([])
      lockRef.current = false
      if (nm.size === deck.length / 2) setPhase('won')
    } else {
      window.setTimeout(() => {
        setFlipped([])
        lockRef.current = false
      }, 900)
    }
  }

  const cancelLoad = () => {
    cancel()
    setPhase('pick')
  }

  if (phase === 'pick')
    return (
      <main className="px-4 py-8">
        {error ? (
          <div className="mx-auto mb-4 max-w-2xl rounded-lg border-2 border-neon-red bg-neon-red/10 px-4 py-2 text-center font-bold text-neon-red">
            {error}
          </div>
        ) : null}
        <TagPicker tags={tags} onStart={start} ctaLabel="START MEMORY" showKind={false} />
      </main>
    )

  if (phase === 'loading' || loading)
    return (
      <GalleryPreview
        modeLabel="MEMORY MODE"
        tags={tagLabels}
        sexuality={formatSexualitySummary(feedPrefs)}
        fraction={progress.fraction}
        count={progress.count}
        note={progress.note}
        onCancel={cancelLoad}
      />
    )

  if (phase === 'won')
    return (
      <main className="grid min-h-[70vh] place-items-center px-4">
        <div className="text-center">
          <div className="graffiti-title">ALL PAIRS FOUND!</div>
          <p className="mt-3 font-comic text-2xl text-kapow">{moves} MOVES</p>
          <button onClick={() => setPhase('pick')} className="btn-goon mt-6 px-10">
            PLAY AGAIN
          </button>
        </div>
      </main>
    )

  const pairs = deck.length / 2
  const found = matched.size

  return (
    <main className="px-3 py-4">
      <div className="mx-auto mb-4 flex max-w-4xl items-center justify-between">
        <h2 className="goon-title text-3xl">MEMORY</h2>
        <div className="font-comic text-lg text-muted">
          <span className="text-pow">{found}</span>/{pairs} PAIRS · <span className="text-kapow">{moves}</span> FLIPS
        </div>
      </div>

      <div className="mx-auto grid max-w-4xl grid-cols-3 gap-2 sm:grid-cols-4 md:gap-3">
        {deck.map((card, i) => {
          const isMatched = matched.has(card.pairId)
          const isRevealed = isMatched || flipped.includes(i)
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => flip(i)}
              disabled={isMatched}
              className={clsx(
                'memory-card',
                isRevealed && 'flipped',
                isMatched && 'matched border-pow/60',
              )}
              aria-label={isRevealed ? `match ${card.item.tag}` : 'hidden card'}
            >
              <div className="memory-inner">
                <div className="memory-face memory-back" aria-hidden={isRevealed}>
                  ?
                </div>
                <div className="memory-face memory-front" aria-hidden={!isRevealed}>
                  <img
                    src={card.item.src}
                    alt=""
                    className="h-full w-full object-cover"
                    draggable={false}
                    loading="lazy"
                  />
                  <div className="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 text-[9px] font-bold uppercase text-pow">
                    {card.item.tag}
                  </div>
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </main>
  )
}